import React, { useCallback, useEffect, useState } from "react";
import { FiEdit3, FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import Swal from "sweetalert2";
import InfiniteScroll from "react-infinite-scroll-component";
import Switch from "react-switch";
import { Loading } from "../../../../../../components/Loading/index.jsx";
import LoadInfiniteScroll from "../../../../../../components/LoadInfiniteScroll/index.jsx";
import Container from "../../../../components/Container/index.jsx";
import ScrollDiv from "../../../../components/ScrollDiv/index.jsx";
import { StyledTooltip } from "../../../../components/Tooltip/index.jsx";
import { BrComerceApiService } from "../../../../../../services/BrComerceApiService.js";
import debounce from "../../../../../../utils/debounce.js";
import PartnerModal from "./PartnerModal.jsx";
import styles from "./styles.module.scss";

const ManagePartners = () => {
  const api = new BrComerceApiService();

  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [actualPage, setActualPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingInfinit, setLoadingInfinit] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const fetchPartners = useCallback(
    async (page = 1, searchText = "") => {
      try {
        setLoadingInfinit(true);
        const response = await api.makeHttpRequest({
          method: "GET",
          url: `/proceda-partners`,
          params: {
            page: page,
            resultsPerPage: 20,
            ...(searchText && { text: searchText })
          }
        });

        if (page === 1) {
          setPartners(response.data);
        } else {
          setPartners(prev => [...prev, ...response.data]);
        }

        setActualPage(response.page);
        setHasMore(response.page < response.pages);
      } catch (error) {
        console.error("Error fetching partners:", error);
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: "Não foi possível carregar os parceiros."
        });
      } finally {
        setLoadingInfinit(false);
        setLoading(false);
      }
    },
    [api]
  );

  const handleSearch = useCallback(
    debounce((searchText) => {
      setActualPage(0);
      setPartners([]);
      fetchPartners(1, searchText);
    }, 500),
    [fetchPartners]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    handleSearch(value);
  };

  const handleClearSearch = () => {
    setSearch("");
    setActualPage(0);
    setPartners([]);
    fetchPartners(1, "");
  };

  const loadMoreData = () => {
    if (!loadingInfinit && hasMore) {
      fetchPartners(actualPage + 1, search);
    }
  };

  const handleCreatePartner = () => {
    setSelectedPartner(null);
    setShowModal(true);
  };

  const handleEditPartner = (partner) => {
    setSelectedPartner(partner);
    setShowModal(true);
  };

  const handleToggleActive = async (partner, currentStatus) => {
    const action = currentStatus ? "desativar" : "ativar";
    const newStatus = !currentStatus;

    try {
      setLoading(true);
      await api.makeHttpRequest({
        method: "PUT",
        url: `/proceda-partners/${partner.id}`,
        data: {
          is_active: newStatus
        }
      });

      Swal.fire({
        title: "Sucesso!",
        text: `Parceiro ${action === "desativar" ? "desativado" : "ativado"} com sucesso.`,
        icon: "success",
        confirmButtonText: "Ok"
      });

      // Atualizar a lista
      setPartners([]);
      setActualPage(0);
      fetchPartners(1, search);
    } catch (error) {
      console.error("Error toggling partner status:", error);
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: `Não foi possível ${action} o parceiro.`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async (partner) => {
    Swal.fire({
      title: "Deseja deletar este parceiro?",
      text: `O parceiro "${partner.name}" será removido permanentemente. Esta ação não pode ser revertida.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Deletar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33"
    }).then(async (result) => {
      if (result.value) {
        try {
          setLoading(true);
          await api.makeHttpRequest({
            method: "DELETE",
            url: `/proceda-partners/${partner.id}`
          });

          Swal.fire({
            title: "Sucesso!",
            text: "Parceiro deletado com sucesso.",
            icon: "success",
            confirmButtonText: "Ok"
          });

          // Atualizar a lista
          setPartners([]);
          setActualPage(0);
          fetchPartners(1, search);
        } catch (error) {
          console.error("Error deleting partner:", error);
          Swal.fire({
            icon: "error",
            title: "Erro!",
            text: "Não foi possível deletar o parceiro."
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleModalClose = (shouldRefresh = false) => {
    setShowModal(false);
    setSelectedPartner(null);

    if (shouldRefresh) {
      setPartners([]);
      setActualPage(0);
      fetchPartners(1, search);
    }
  };

  useEffect(() => {
    window.setPageTitle("Integrações | Proceda - Gerenciar Parceiros");
    setLoading(true);
    fetchPartners(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Loading loading={loading} />
      <Container title="Gerenciar Parceiros Proceda">
        <div className={styles.filtersWrapper}>
          <div className={styles.searchContainer}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por nome ou código..."
              value={search}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
            {search && (
              <button onClick={handleClearSearch} className={styles.clearButton}>
                <FiX size={18} />
              </button>
            )}
          </div>
          <button className={styles.filterBtn} onClick={handleCreatePartner}>
            <FiPlus size={18} />
            <span>Novo Parceiro</span>
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <ScrollDiv data={partners}>
            <InfiniteScroll
              dataLength={partners.length}
              next={loadMoreData}
              hasMore={hasMore}
              loader={loadingInfinit && <LoadInfiniteScroll />}
              scrollableTarget="scrollDiv"
            >
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead className={styles.tableHead}>
                    <tr>
                      <th>Ações</th>
                      <th>Nome</th>
                      <th>Código</th>
                      <th>Ambiente</th>
                      <th>Host</th>
                      <th>Ativo</th>
                    </tr>
                  </thead>
                  <tbody className={styles.tableBody}>
                    {partners.length === 0 && !loading && (
                      <tr>
                        <td colSpan="6" className={styles.emptyState}>
                          Nenhum parceiro encontrado.
                        </td>
                      </tr>
                    )}
                    {partners.map((partner) => (
                      <tr key={partner.id}>
                        <td>
                          <div className={styles.actions}>
                            <StyledTooltip text="Editar" position="top" arrow>
                              <button onClick={() => handleEditPartner(partner)}>
                                <FiEdit3 size={20} color="#409E6A" />
                              </button>
                            </StyledTooltip>
                            <StyledTooltip text="Deletar" position="top" arrow>
                              <button onClick={() => handleDeletePartner(partner)}>
                                <FiTrash2 size={20} color="#d32f2f" />
                              </button>
                            </StyledTooltip>
                          </div>
                        </td>
                        <td>
                          <span className={styles.partnerName}>{partner.name}</span>
                        </td>
                        <td>
                          <span>{partner.code}</span>
                        </td>
                        <td>
                          <span className={styles[`environment${partner.environment}`]}>
                            {partner.environment === "PROD" ? "Produção" : "Homologação"}
                          </span>
                        </td>
                        <td>
                          <span>{partner.host || "--"}</span>
                        </td>
                        <td>
                          <div className={styles.statusContainer}>
                            <Switch
                              checked={partner.is_active}
                              onChange={() => handleToggleActive(partner, partner.is_active)}
                              offColor="#B3B3B3"
                              onColor="#409E6A"
                              offHandleColor="#FFF"
                              onHandleColor="#FFF"
                              uncheckedIcon={false}
                              checkedIcon={false}
                              width={45}
                              height={23}
                              handleDiameter={13}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </InfiniteScroll>
          </ScrollDiv>
        </div>
      </Container>

      {showModal && (
        <PartnerModal
          partner={selectedPartner}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export { ManagePartners };
